import { FETCH_DRIVE_FILE } from "@lib/graphql/mutations";
import { GOOGLE_DRIVE_PICKER_QUERY } from "@lib/graphql/queries";
import { useBackgroundTasksStore } from "@lib/stores/backgroundTasksStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";
import { useCurrentUserGoogleIntegration } from "@/contexts/CurrentUserGoogleIntegrationContext";

// Picker JS lives on a global script. We lazy-load + memoise the load promise
// so re-opening doesn't re-inject the tag.
const GAPI_SRC = "https://apis.google.com/js/api.js";
let gapiLoadPromise: Promise<void> | null = null;

interface GApiWindow extends Window {
  gapi?: {
    load: (name: "picker", cb: () => void) => void;
  };
  google?: {
    picker: GooglePickerNs;
  };
}

interface GooglePickerNs {
  DocsView: new () => DocsView;
  PickerBuilder: new () => PickerBuilder;
  ViewId: { DOCS: number };
  Action: { PICKED: string; CANCEL: string };
  Feature: { MULTISELECT_ENABLED: string; SUPPORT_DRIVES: string };
}

interface DocsView {
  setIncludeFolders: (v: boolean) => DocsView;
  setSelectFolderEnabled: (v: boolean) => DocsView;
  setOwnedByMe: (v: boolean) => DocsView;
  setEnableDrives: (v: boolean) => DocsView;
  setMimeTypes: (v: string) => DocsView;
}

interface PickerBuilder {
  addView: (view: DocsView) => PickerBuilder;
  setOAuthToken: (t: string) => PickerBuilder;
  setDeveloperKey: (k: string) => PickerBuilder;
  setAppId: (id: string) => PickerBuilder;
  setCallback: (cb: (data: PickerCallbackData) => void) => PickerBuilder;
  enableFeature: (feature: string) => PickerBuilder;
  build: () => { setVisible: (v: boolean) => void };
}

interface PickerCallbackData {
  action: string;
  docs?: Array<{ id: string; name?: string; mimeType?: string }>;
}

function loadGApi(): Promise<void> {
  if (gapiLoadPromise) return gapiLoadPromise;
  gapiLoadPromise = new Promise<void>((resolve, reject) => {
    const win = window as GApiWindow;
    if (win.gapi) return resolve();
    const script = document.createElement("script");
    script.src = GAPI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google API script"));
    document.head.appendChild(script);
  });
  return gapiLoadPromise;
}

function loadPicker(): Promise<GooglePickerNs> {
  return loadGApi().then(
    () =>
      new Promise<GooglePickerNs>((resolve, reject) => {
        const win = window as GApiWindow;
        if (!win.gapi) return reject(new Error("gapi not available"));
        win.gapi.load("picker", () => {
          const ns = (window as GApiWindow).google?.picker;
          if (!ns) return reject(new Error("Google Picker namespace missing"));
          resolve(ns);
        });
      }),
  );
}

// FileDropZone's accept format mixes extensions (".pdf"), MIME types
// ("image/png"), and wildcards ("image/*"). Google Picker only takes MIME
// types — map common extensions to MIMEs and drop bare wildcards (the
// post-pick blob carries the correct content_type either way).
const EXT_TO_MIME: Record<string, string> = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".svg": "image/svg+xml",
  ".webp": "image/webp",
  ".txt": "text/plain",
};

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DOC_LEGACY_MIME = "application/msword";
const GOOGLE_DOC_MIME = "application/vnd.google-apps.document";
const GOOGLE_SHEET_MIME = "application/vnd.google-apps.spreadsheet";

export function acceptToMimeTypes(accept: string | undefined): string {
  if (!accept) return "";
  const out = new Set<string>();
  for (const raw of accept.split(",")) {
    const tok = raw.trim().toLowerCase();
    if (!tok) continue;
    if (tok.startsWith(".")) {
      const mime = EXT_TO_MIME[tok];
      if (mime) out.add(mime);
    } else if (!tok.endsWith("/*")) {
      // Picker rejects wildcards; pass through bare MIME types only.
      out.add(tok);
    }
  }
  // Show legacy .doc files when .docx is accepted so the user sees them
  // instead of an empty folder. They're rejected on pick with a conversion hint.
  if (out.has(DOCX_MIME)) {
    out.add(DOC_LEGACY_MIME);
    out.add(GOOGLE_DOC_MIME);
  }
  if (out.has(XLSX_MIME)) out.add(GOOGLE_SHEET_MIME);
  return [...out].join(",");
}

export interface DrivePickerButtonProps {
  accept?: string;
  disabled?: boolean;
  label?: string;
  onSignedId: (signedId: string, filename: string, contentType: string | null) => void;
}

type Phase = "idle" | "loading" | "picking" | "fetching";

export function DrivePickerButton({
  accept,
  disabled = false,
  label = "Choose from Google Drive",
  onSignedId,
}: DrivePickerButtonProps) {
  const { integration, fetching: integrationFetching } = useCurrentUserGoogleIntegration();
  const [{ data: pickerData, fetching: pickerFetching, error: pickerError }, refetchPicker] =
    useQuery({
      query: GOOGLE_DRIVE_PICKER_QUERY,
      pause: true,
      requestPolicy: "network-only",
    });
  const [, fetchDriveFile] = useMutation(FETCH_DRIVE_FILE);

  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const onSignedIdRef = useRef(onSignedId);
  onSignedIdRef.current = onSignedId;
  // Set when the user clicks and we're waiting for the picker session query to
  // resolve. Cleared when we either open the picker or redirect to OAuth.
  const awaitingPickerRef = useRef(false);

  const mimeTypes = useMemo(() => acceptToMimeTypes(accept), [accept]);

  // While a fetch is in flight, watch the BackgroundTask store for completion.
  const task = useBackgroundTasksStore((s) => (taskId ? s.tasksById[taskId] : undefined));
  useEffect(() => {
    if (!taskId || !task) return;
    if (task.state === "succeeded") {
      const result = task.result ?? {};
      const signedId = result.signed_id;
      const filename = (result.filename as string) ?? "drive-file";
      const contentType = (result.content_type as string | null) ?? null;
      if (typeof signedId === "string") {
        onSignedIdRef.current(signedId, filename, contentType);
      } else {
        setError("Drive import finished but no file id was returned.");
      }
      setPhase("idle");
      setTaskId(null);
    } else if (task.state === "failed") {
      setError(task.errorMessage ?? "Could not import from Google Drive.");
      setPhase("idle");
      setTaskId(null);
    }
  }, [taskId, task]);

  const connectUrl = useMemo(() => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return `/oauth/google/connect?return_to=${encodeURIComponent(returnTo)}`;
  }, []);

  const openPickerWithSession = useCallback(
    async (session: { accessToken: string; appId: string; developerKey: string }) => {
      setPhase("loading");
      let picker: GooglePickerNs;
      try {
        picker = await loadPicker();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load Google Picker.");
        setPhase("idle");
        return;
      }

      const buildView = () => {
        // Show folders so users can navigate into them, but don't let a
        // folder itself be returned as the picked result.
        const v = new picker.DocsView().setIncludeFolders(true).setSelectFolderEnabled(false);
        if (mimeTypes) v.setMimeTypes(mimeTypes);
        return v;
      };

      const myDrive = buildView();
      const sharedWithMe = buildView().setOwnedByMe(false);
      const sharedDrives = buildView().setEnableDrives(true);

      const built = new picker.PickerBuilder()
        .addView(myDrive)
        .addView(sharedWithMe)
        .addView(sharedDrives)
        .enableFeature(picker.Feature.SUPPORT_DRIVES)
        .setOAuthToken(session.accessToken)
        .setDeveloperKey(session.developerKey)
        .setAppId(session.appId)
        .setCallback((cbData) => {
          if (cbData.action === picker.Action.PICKED) {
            const doc = cbData.docs?.[0];
            if (!doc) {
              setPhase("idle");
              return;
            }
            if (doc.mimeType === DOC_LEGACY_MIME) {
              setError(
                "This file uses the legacy .doc format which is not supported. " +
                  "Open it in Google Docs to convert it, then select the converted version.",
              );
              setPhase("idle");
              return;
            }
            setPhase("fetching");
            fetchDriveFile({ input: { driveFileId: doc.id } })
              .then((res) => {
                const errs = res.data?.fetchDriveFile?.errors as string[] | undefined;
                const task = res.data?.fetchDriveFile?.backgroundTask as { id: string } | undefined;
                if (errs && errs.length > 0) {
                  setError(errs[0]);
                  setPhase("idle");
                  return;
                }
                if (!task) {
                  setError("Drive import did not start.");
                  setPhase("idle");
                  return;
                }
                setTaskId(task.id);
              })
              .catch((e: unknown) => {
                setError(e instanceof Error ? e.message : "Could not import from Drive.");
                setPhase("idle");
              });
          } else if (cbData.action === picker.Action.CANCEL) {
            setPhase("idle");
          }
        })
        .build();

      setPhase("picking");
      built.setVisible(true);
    },
    [fetchDriveFile, mimeTypes],
  );

  // When refetchPicker resolves after a click, branch on the result: open the
  // picker, or redirect to OAuth if the session isn't available.
  useEffect(() => {
    if (!awaitingPickerRef.current) return;
    if (pickerFetching) return;
    awaitingPickerRef.current = false;

    if (pickerError) {
      setError("Could not start Google Drive picker.");
      return;
    }
    const session = pickerData?.googleDrivePicker as
      | { accessToken: string; appId: string; developerKey: string }
      | null
      | undefined;
    if (!session) {
      window.location.href = connectUrl;
      return;
    }
    void openPickerWithSession(session);
  }, [pickerData, pickerFetching, pickerError, connectUrl, openPickerWithSession]);

  const handleClick = useCallback(() => {
    setError(null);
    awaitingPickerRef.current = true;
    // Fetch a fresh picker session every time — the token is short-lived.
    refetchPicker({ requestPolicy: "network-only" });
  }, [refetchPicker]);

  if (!integrationFetching && !integration) {
    return (
      <a
        href={connectUrl}
        className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
      >
        Connect Google Drive
      </a>
    );
  }

  const isBusy = phase !== "idle";
  const buttonLabel =
    phase === "loading"
      ? "Loading picker…"
      : phase === "picking"
        ? "Choose a file…"
        : phase === "fetching"
          ? "Importing from Drive…"
          : label;

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isBusy}
        className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {buttonLabel}
      </button>
      {error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
}
